// NewSidebar/NewSideBar.jsx
import React, { useEffect, useState } from "react";
import menuIconSvg from "../../asset/Icons/menuIcon.svg";
import "./style/SideBar.css";
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { motion, AnimatePresence } from "framer-motion";
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import { useTranslation } from 'react-i18next';
import { Box, Drawer, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import Item from "./NewSidebar/SidebarItem";       // همان Item فعلی شما
import Submenu from "./NewSidebar/SidebarSubmenu";  // همان Submenu فعلی شما
import iconMap from "./NewSidebar/iconMap";
import usePermissionEngine from "./NewSidebar/usePermissionEngine";
import sidebarMenuData from "./NewSidebar/sidebarMenu.json";
import useDeviceType from "./Hooks/useDeviceType";
import { useLocation } from "react-router-dom";
import LogOutIcon from "./Icon/LogOutIcon";

function NewSideBar({ mobileOpen, onClose }) {
  /* ─── Location change → close mobile drawer ─── */
  const { i18n } = useTranslation();
  const isRtl = i18n.dir() === 'rtl';

  const location = useLocation();
  const [prevPath, setPrevPath] = useState(location.pathname);
  useEffect(() => {
    if (location.pathname !== prevPath) onClose?.();
    setPrevPath(location.pathname);
  }, [location.pathname]);

  /* ─── Sidebar toggle (desktop) ─── */
  const [sidebar, setSidebar] = useState(true);
  const toggleSideBar = () => setSidebar((prev) => !prev);

  /* ─── Selected state ─── */
  const [selected, setSelected] = useState("داشبورد");

  /* ─── Device type ─── */
  const deviceType = useDeviceType();

  /* ═══════════════════════════════════════════
     ★ Permission Engine — فیلتر منو
     ═══════════════════════════════════════════ */
  const filteredMenu = usePermissionEngine(sidebarMenuData.menu);

  /* ─── رندر بازگشتی آیتم‌ها ─── */
  const renderMenuItem = (item) => {
    // --- آیتم Parent (دارای children) ---
    if (item.children && item.children.length > 0) {
      return (
        <Submenu
          key={item.key}
          selected={selected}
          sidebar={sidebar}
          title={item.title}
          keyEn={item.keyEn || item.key}
          icon={iconMap[item.icon] || null}
        >
          {item.children.map((child) => renderMenuItem(child))}
        </Submenu>
      );
    }

    // --- آیتم ساده (Leaf) ---
    return (
      <Item
        key={item.key}
        selected={selected}
        title={item.title}
        setSelected={setSelected}
        to={item.to}
        icon={iconMap[item.icon] || null}
        smallpadding={item.smallpadding}
        submenu={item.submenu}
        sidebar={sidebar}
      />
    );
  };

  /* ─── Logout Item (همیشه نمایش داده می‌شود) ─── */
  const logoutItem = (
    <Item
      key="logout"
      selected={selected}
      title="خروج"
      setSelected={setSelected}
      icon={<LogOutIcon />}
      smallpadding={true}
      sidebar={sidebar}
    />
  );

  /* ══════════════════════════════════════════════
     📱 MOBILE SIDEBAR
     ══════════════════════════════════════════════ */
  if (deviceType === "mobile") {
    return (
      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={onClose}
        PaperProps={{
          sx: {
            width: "80%",
            maxWidth: "330px",
            background: "transparent",
            boxShadow: "none",
          },
        }}
      >
        <Box
          className="sidebar-cls"
          sx={{
            width: "100%",
            borderRadius: "24px 0 0 24px",
            height: "100vh",
            overflowY: "auto",
            p: 2,
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <IconButton onClick={onClose}>
              <CloseIcon sx={{ color: "#fff" }} />
            </IconButton>
          </Box>
          <Box sx={{ mt: 2 }}>
            {filteredMenu.map(renderMenuItem)}
            {logoutItem}
          </Box>
        </Box>
      </Drawer>
    );
  }

  /* ══════════════════════════════════════════════
     🖥️ DESKTOP SIDEBAR
     ══════════════════════════════════════════════ */
  return (
    <div
      style={{ width: sidebar ? "15rem" : "6rem" }}
      className="sidebar-cls"
    >


<Box sx={{ width: '100%', display: 'flex', justifyContent: 'flex-end', px: 1 }}>
  <motion.div
    whileHover={{ scale: 1.08 }}
    whileTap={{ scale: 0.92 }}
    style={{ borderRadius: '50%' }}
  >
    <IconButton
      onClick={toggleSideBar}
      sx={{
        position: 'relative',
        overflow: 'hidden',
        marginTop:1,
        backdropFilter: 'blur(8px)',
        background: 'rgba(255,255,255,0.08)',
        border: '1px solid rgba(255,255,255,0.15)',
        transition: 'background 0.25s, border-color 0.25s',
        '&:hover': {
          background: 'rgba(255,255,255,0.18)',
          borderColor: 'rgba(255,255,255,0.35)',
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          borderRadius: '50%',
          background: 'radial-gradient(circle at 30% 30%, rgba(255,255,255,0.18), transparent 70%)',
          pointerEvents: 'none',
        },
      }}
    >
      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={sidebar ? 'next' : 'before'}
          initial={{ opacity: 0, rotate: -90, scale: 0.5 }}
          animate={{ opacity: 1, rotate: 0, scale: 1 }}
          exit={{ opacity: 0, rotate: 90, scale: 0.5 }}
          transition={{ duration: 0.22, ease: [0.4, 0, 0.2, 1] }}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          {sidebar
            ? <ArrowForwardIosIcon sx={{ color: '#2A4405',fontSize:'1.2rem'}} />
            : <ArrowBackIosIcon sx={{ color: 'white',fontSize:'1.2rem'}}   />
          }
        </motion.div>
      </AnimatePresence>
    </IconButton>
  </motion.div>
</Box>


      <ul
        style={{
          width: "100%",
          padding: "15px",
          display: "flex",
          flexDirection: "column",
          alignItems: sidebar ? "flex-start" : "center",
        }}
      >
        {filteredMenu.map(renderMenuItem)}
        {logoutItem}
      </ul>
    </div>
  );
}

export default NewSideBar;
